import Page from '../../components/Page'
import { Button, Result, Tag, message } from 'antd'
import { useTranslation } from 'react-i18next'
import Table from '../../components/Table'
import { useState } from 'react'
import { apiFetch } from '../../api'
import UserDetailDrawer from './UserDetailDrawer'

export default () => {
  useTranslation()
  const [messageApi, contextHolder] = message.useMessage()

  const [pageError, setPageError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)
  // HOR-75: Kunde, dessen Detailansicht offen ist.
  const [detailUserId, setDetailUserId] = useState(null)

  /**
   * Result handler for edit (HOR-5, follows the HOR-4 Articles pattern).
   * Shows a specific translated message and always returns true so the table wrapper's
   * generic "request failed" message is suppressed. The wrapper refetches the list after
   * every mutation regardless of the return value, so a stale row (404) disappears
   * automatically.
   */
  const handleMutationResult = (result, successKey) => {
    if (result.errorType === 'unauthorized') return true // apiFetch already redirected to login

    if (result.ok) {
      messageApi.open({
        type: 'success',
        content: i18n(successKey),
      })

      return true
    }

    if (result.status === 400 && result.data?.message === 'cannotChangeOwnRole') {
      // lockout protection: an admin must not demote themselves – the backend answers
      // with a string message here (validation 400s carry a message array instead)
      messageApi.open({
        type: 'error',
        content: i18n('users.cannotChangeOwnRole'),
      })
    } else if (result.status === 400) {
      // class-validator answers with { message: [...] } – show one translated summary
      // instead of the raw English backend strings
      messageApi.open({
        type: 'error',
        content: i18n('users.validationFailed'),
      })
    } else if (result.errorType === 'forbidden') {
      messageApi.open({
        type: 'error',
        content: i18n('errors.noPermission'),
      })
    } else if (result.status === 404) {
      // user was deleted in the meantime – the wrapper's refetch removes the stale row
      messageApi.open({
        type: 'error',
        content: i18n('errors.notFound'),
      })
    } else if (result.errorType === 'network') {
      messageApi.open({
        type: 'error',
        content: i18n('errors.network'),
      })
    } else {
      messageApi.open({
        type: 'error',
        content: i18n('common.requestFailed'),
      })
    }

    return true
  }

  const columns = [
    {
      title: i18n('users.columns.registered'),
      key: 'createdAt',
      dataIndex: 'createdAt',
      readonly: true,
      // HOR-90: Pro-Table uebergibt bei ellipsis ein React-Element als erstes Argument –
      // moment(<Element>) ergab "heute 00:00" fuer jede Zeile. Rohwert aus dem Record lesen.
      render: (_, record) => moment(record.createdAt).fromNow(),
    },
    {
      title: i18n('users.columns.username'),
      key: 'name',
      dataIndex: 'name',
      formItemProps: {
        rules: [
          {
            required: true,
            // whitespace-only names ("   ") must not pass the required rule
            whitespace: true,
            message: i18n('users.nameRequired'),
          },
          {
            min: 3,
            max: 20,
            message: i18n('users.nameLength'),
          },
        ],
      },
    },
    {
      title: i18n('users.columns.email'),
      key: 'email',
      dataIndex: 'email',
      readonly: true,
    },
    {
      title: i18n('users.columns.role'),
      key: 'role',
      dataIndex: 'role',
      valueType: 'select',
      valueEnum: {
        user: { text: i18n('users.roles.user'), status: 'Default' },
        administrator: { text: i18n('users.roles.administrator'), status: 'Warning' },
      },
      // ProTable passes the rendered valueEnum dom as first argument – read the raw
      // role from the record so the Tag shows the translated label
      render: (_, { role }) => <Tag color={role === 'administrator' ? 'gold' : 'default'}>{i18n(`users.roles.${role}`)}</Tag>,
    },
    {
      title: i18n('users.columns.lastLogin'),
      readonly: true,
      key: 'lastLoginAt',
      dataIndex: 'lastLoginAt',
      // HOR-90: Rohwert aus dem Record (siehe Spalte "Registriert"); ohne Wert oder mit
      // ungueltigem Datum wieder "Noch nie" – moment(<Element>).isValid() war immer true.
      render: (_, record) => {
        if (!record.lastLoginAt || !moment(record.lastLoginAt).isValid()) return i18n('users.columns.never')

        return moment(record.lastLoginAt).fromNow()
      },
    },
    {
      // HOR-75: Bis hierhin fuehrte der einzige Weg zu den Kundendaten ueber
      // Impersonate - also ueber das Oeffnen der Kunden-App als dieser Nutzer.
      title: i18n('users.columns.details'),
      readonly: true,
      key: 'details',
      dataIndex: 'details',
      render: (_, val) => (
        <a style={{ userSelect: 'none' }} onClick={() => setDetailUserId(val.id)}>
          {i18n('users.detail.open')}
        </a>
      ),
    },
    {
      title: i18n('users.columns.admin'),
      readonly: true,
      key: 'impersonate',
      dataIndex: 'impersonate',
      render: (_, val) => {
        return (
          <a
            style={{ userSelect: 'none' }}
            onClick={async () => {
              const result = await apiFetch(`/user/impersonate/${val.id}`, {
                method: 'POST',
              })

              if (result.errorType === 'unauthorized') return // apiFetch already redirected to login

              // deliberate trade-off (HOR-5): the window is opened only *after* the
              // request succeeded, so a failed impersonate never opens a broken
              // login?jwt=undefined tab. Browsers keep the user activation alive for a
              // few seconds, so the popup normally still passes the blocker; if a very
              // slow response gets it blocked, the click can simply be repeated.
              if (result.ok && result.data?.jwt) {
                window.open(`https://app.horoluma.com/login?jwt=${result.data.jwt}`, '_blank')

                return
              }

              messageApi.open({
                type: 'error',
                content: i18n('users.impersonateFailed'),
              })
            }}
          >
            {i18n('users.impersonate')}
          </a>
        )
      },
    },
  ]

  return (
    <Page title={i18n('users.title')}>
      {contextHolder}
      <UserDetailDrawer userId={detailUserId} open={detailUserId !== null} onClose={() => setDetailUserId(null)} />
      {pageError && (
        <Result
          status={pageError === 'forbidden' ? '403' : pageError === 'network' ? 'warning' : 'error'}
          title={pageError === 'forbidden' ? i18n('errors.noPermission') : pageError === 'network' ? i18n('errors.network') : i18n('errors.loadFailed')}
          extra={
            // retrying a 403 will not help – only offer retry for transient errors (HOR-1 pattern)
            pageError !== 'forbidden' && (
              <Button
                type="primary"
                onClick={() => {
                  setPageError(null)
                  setReloadKey(key => key + 1)
                }}
              >
                {i18n('common.retry')}
              </Button>
            )
          }
        />
      )}
      {!pageError && (
        <Table
          key={reloadKey}
          columns={columns}
          modes={['edit']}
          title={i18n('users.tableTitle')}
          onEdit={async user => {
            const result = await apiFetch(`/user/${user.id}`, {
              method: 'PATCH',
              body: {
                name: user.name,
                role: user.role,
              },
            })

            return handleMutationResult(result, 'users.saved')
          }}
          request={async () => {
            const result = await apiFetch('/user/all')

            if (!result.ok) {
              setPageError(result.errorType)

              return {
                data: [],
                total: 0,
                success: false,
              }
            }

            const users = Array.isArray(result.data) ? result.data : []

            return {
              data: users,
              total: users.length,
              success: true,
            }
          }}
        />
      )}
    </Page>
  )
}
